import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { runtimeAPI, containersAPI } from '@hpanel/web-common/api/client'
import type { CatalogImage, Container, CurrentRuntime } from '@hpanel/web-common/api/types'
import { SiteUpdatesPanel } from '../../components/client/SiteUpdatesPanel'

/**
 * Tenant Runtime Picker (Plan 4).
 *
 * One image picker per owned container. Swap stops the container,
 * auto-snapshots (rollback handle), and recreates against the new
 * image. Volume preserved.
 */
export const RuntimePicker: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedId = searchParams.get('container')
  const [containers, setContainers] = useState<Container[]>([])
  const [selectedContainerId, setSelectedContainerId] = useState<number | null>(null)
  const [current, setCurrent] = useState<CurrentRuntime | null>(null)
  const [available, setAvailable] = useState<CatalogImage[]>([])
  const [chosenImageId, setChosenImageId] = useState<number | ''>('')
  const [takeSnapshot, setTakeSnapshot] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const list = await containersAPI.list()
        if (cancelled) return
        setContainers(list.containers)
        const selected = requestedId === null ? list.containers[0] : list.containers.find(c => String(c.id) === requestedId)
        setSelectedContainerId(selected?.id ?? null)
        setError(!selected && requestedId !== null ? 'Requested container is unavailable. Select another container.' : null)
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load containers')
      }
    })()
    return () => { cancelled = true }
  }, [requestedId])

  useEffect(() => {
    let cancelled = false
    setCurrent(null)
    setAvailable([])
    setChosenImageId('')
    setInfo(null)
    if (selectedContainerId === null) return
    void (async () => {
      try {
        const c = await runtimeAPI.getCurrent(selectedContainerId)
        if (cancelled) return
        setCurrent(c)
        // Filter images to the same backend kind.
        const all = await runtimeAPI.listAvailable(c.backend_kind as 'docker' | 'lxc')
        if (!cancelled) setAvailable(all)
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load runtime')
      }
    })()
    return () => { cancelled = true }
  }, [selectedContainerId])

  const swap = async () => {
    if (!selectedContainerId || chosenImageId === '') return
    if (
      !window.confirm(
        'Swap runtime now?\n\nYour container will stop, take a pre-swap snapshot, recreate against the new image, and start. Volume preserved. Brief downtime (2–15 seconds).',
      )
    ) {
      return
    }
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      const result = await runtimeAPI.swap(selectedContainerId, {
        image_id: Number(chosenImageId),
        take_snapshot: takeSnapshot,
      })
      setInfo(
        result.swapped
          ? `Swap complete. Snapshot id: ${result.snapshot_id ?? '—'}`
          : 'Already on this image — no-op.',
      )
      // Re-fetch current
      const c = await runtimeAPI.getCurrent(selectedContainerId)
      setCurrent(c)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Swap failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div data-testid="runtime-picker-page" className="space-y-6">
      <div>
        <h1 data-testid="page-title" className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          Runtime
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Pick the language stack and version your container runs.
          Swapping preserves your <code>/home/&lt;user&gt;/</code> files
          and your databases.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-300 text-red-800 dark:text-red-200 rounded p-3">
          {error}
        </div>
      )}
      {info && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 text-green-800 dark:text-green-200 rounded p-3">
          {info}
        </div>
      )}

      {containers.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4">
          {containers.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Container
              </label>
              <select
                value={selectedContainerId ?? ''}
                onChange={(e) => setSearchParams({ container: e.target.value })}
                disabled={busy}
                aria-label="Container"
                className="mt-1 w-full max-w-xs px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
              >
                <option value="" disabled>Select a container</option>
                {containers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {current && (
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Current image</p>
              <p className="font-mono mt-1">{current.image}</p>
              {current.catalog_image && (
                <p className="text-sm mt-1">
                  {current.catalog_image.name} —{' '}
                  {current.catalog_image.runtime_kind}{' '}
                  {current.catalog_image.runtime_version}
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Available images
            </label>
            <select
              value={chosenImageId}
              onChange={(e) => setChosenImageId(e.target.value === '' ? '' : Number(e.target.value))}
              data-testid="runtime-picker-image-select"
              className="mt-1 w-full max-w-xl px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
            >
              <option value="">— pick an image —</option>
              {available.map((img) => (
                <option key={img.id} value={img.id}>
                  {img.name} ({img.runtime_kind} {img.runtime_version}
                  {img.status === 'default' && ' · default'})
                </option>
              ))}
            </select>
            {available.length === 0 && (
              <p className="text-sm text-gray-500 mt-2">
                No images in the catalog yet — ask your administrator to publish one.
              </p>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={takeSnapshot}
              onChange={(e) => setTakeSnapshot(e.target.checked)}
            />
            Take a pre-swap snapshot (recommended)
          </label>

          <button
            onClick={swap}
            disabled={busy || chosenImageId === ''}
            data-testid="runtime-picker-swap-button"
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg"
          >
            {busy ? 'Swapping…' : 'Swap runtime'}
          </button>
        </div>
      )}

      {selectedContainerId !== null && <SiteUpdatesPanel containerId={selectedContainerId} />}

      {containers.length === 0 && (
        // A bare "you have nothing" line is a dead end — the tenant is left
        // with no way forward from this page. Containers are provisioned by
        // creating a domain, so point there. (Flagged 2026-07-29 by the QA
        // validator's visual judge, which passed the Backups and WordPress
        // empty states precisely because they offer a next step.)
        <div
          data-testid="runtime-empty-state"
          className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 text-center"
        >
          <p className="text-gray-900 dark:text-gray-100 font-medium">
            You don't have any containers yet
          </p>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1 mb-4">
            A container is created for you when an administrator approves your first site.
            Once it exists, you can swap its language stack here.
          </p>
          <a
            href="/client/sites/request"
            data-testid="runtime-empty-cta"
            className="inline-block px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
          >
            Request a site
          </a>
        </div>
      )}
    </div>
  )
}
